import React, { useEffect, useState } from 'react';
import { API_BASE_URL } from '../services/config';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material';
import { Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Save } from '@mui/icons-material';
import { companyService } from '../services/companyService';


interface SubcontractorFormData {
  companyName: string;
  contactPerson: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  licenseNumber: string;
  insuranceProvider: string;
  insuranceExpiry: string;
  specialties: string[];
  rating: number;
  status: string;
  notes: string;
}

const SubcontractorCreate: React.FC = () => {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [companyId, setCompanyId] = useState<string>('');
  const [formData, setFormData] = useState<SubcontractorFormData>({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    licenseNumber: '',
    insuranceProvider: '',
    insuranceExpiry: '',
    specialties: [],
    rating: 0,
    status: 'Active',
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newSpecialty, setNewSpecialty] = useState('');

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const items = await companyService.getAllCompanies();
        const mapped = (Array.isArray(items) ? items : []).map((c: any) => ({ id: c.id, name: c.name }));
        setCompanies(mapped);
        if (mapped.length === 1) {
          setCompanyId(mapped[0].id);
        }
      } catch (err) {
        console.warn('Failed to load companies for subcontractor form', err);
      }
    };
    fetchCompanies();
  }, []);

  const availableSpecialties = [
    'Electrical',
    'Plumbing',
    'HVAC',
    'Roofing',
    'Flooring',
    'Painting',
    'Concrete',
    'Framing',
    'Drywall',
    'Landscaping',
    'Excavation',
    'Masonry',
    'Insulation',
    'Windows & Doors',
    'Demolition',
  ];

  const statuses = [
    'Active',
    'Inactive',
    'Pending Approval',
    'Suspended',
  ];

  const handleInputChange = (field: keyof SubcontractorFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'rating' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSelectChange = (field: keyof SubcontractorFormData) => (
    event: any
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAddSpecialty = () => {
    if (newSpecialty && !formData.specialties.includes(newSpecialty)) {
      setFormData(prev => ({
        ...prev,
        specialties: [...prev.specialties, newSpecialty],
      }));
      setNewSpecialty('');
    }
  };

  const handleRemoveSpecialty = (specialty: string) => {
    setFormData(prev => ({
      ...prev,
      specialties: prev.specialties.filter(s => s !== specialty),
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const payload = {
        company: companyId || undefined,
        companyName: formData.companyName,
        contactPerson: formData.contactPerson,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        licenseNumber: formData.licenseNumber,
        insuranceProvider: formData.insuranceProvider,
        insuranceExpiry: formData.insuranceExpiry,
        specialties: formData.specialties,
        rating: formData.rating,
        status: formData.status,
        notes: formData.notes,
      };
      const response = await fetch(`${API_BASE_URL}/subcontractors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to create subcontractor');
      }

      navigate('/subcontractors');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/subcontractors')}
          sx={{ textDecoration: 'none' }}
        >
          Subcontractors
        </Link>
        <Typography color="text.primary">Create Subcontractor</Typography>
      </Breadcrumbs>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/subcontractors')}
          sx={{ mr: 2 }}
        >
          Back to Subcontractors
        </Button>
        <Typography variant="h4" component="h1">
          Create New Subcontractor
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Company Information
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Company</InputLabel>
                  <Select
                    value={companyId}
                    onChange={(e) => setCompanyId(e.target.value)}
                    label="Company"
                  >
                    {companies.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={formData.companyName}
                  onChange={handleInputChange('companyName')}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={handleInputChange('contactPerson')}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange('email')}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={handleInputChange('phone')}
                  required
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Address
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={handleInputChange('address')}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.city}
                  onChange={handleInputChange('city')}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="State"
                  value={formData.state}
                  onChange={handleInputChange('state')}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={formData.zipCode}
                  onChange={handleInputChange('zipCode')}
                />
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Licensing & Insurance
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="License Number"
                  value={formData.licenseNumber}
                  onChange={handleInputChange('licenseNumber')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Insurance Provider"
                  value={formData.insuranceProvider}
                  onChange={handleInputChange('insuranceProvider')}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Insurance Expiry Date"
                  type="date"
                  value={formData.insuranceExpiry}
                  onChange={handleInputChange('insuranceExpiry')}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleSelectChange('status')}
                    label="Status"
                  >
                    {statuses.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Specialties
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid size={{ xs: 12, md: 8 }}>
                <FormControl fullWidth>
                  <InputLabel>Add Specialty</InputLabel>
                  <Select
                    value={newSpecialty}
                    onChange={(e) => setNewSpecialty(e.target.value)}
                    label="Add Specialty"
                  >
                    {availableSpecialties
                      .filter(specialty => !formData.specialties.includes(specialty))
                      .map((specialty) => (
                        <MenuItem key={specialty} value={specialty}>
                          {specialty}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleAddSpecialty}
                  disabled={!newSpecialty}
                  fullWidth
                >
                  Add Specialty
                </Button>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.specialties.map((specialty) => (
                <Chip
                  key={specialty}
                  label={specialty}
                  onDelete={() => handleRemoveSpecialty(specialty)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>

            <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
              Additional Information
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Rating (1-5)"
                  type="number"
                  value={formData.rating}
                  onChange={handleInputChange('rating')}
                  inputProps={{ min: 0, max: 5, step: 0.1 }}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleInputChange('notes')}
                  multiline
                  rows={4}
                  placeholder="Additional notes about the subcontractor"
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Subcontractor'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/subcontractors')}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SubcontractorCreate;